import { QueryKeys } from '@/enums/index';
import { INextAuthSessionModel } from '@/interfaces/authentication';
import {
  IBrandList,
  IProductDetails,
  IProductList,
  IProductListPayload,
  IProductModel,
  ISearchProduct,
  IWishListProducts,
} from '@/interfaces/product';
import {
  getBrandList,
  getCrossCategorySuggestionsList,
  getMemberExclusive,
  getPopularSearches,
  getProduct,
  getProductInfoGraphic,
  getProducts,
  getRecentlyViewedProducts,
  getRecommendedProductList,
  getWishlist,
  memberExclusiveList,
  postProductInfoGraphic,
  searchAutoComplete,
  searchProduct,
} from '@/services/product.service';
import useHeaderStore from '@/store/header';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
// import { useReactQuerySession } from '@/app/hooks/reactQuerySession';

export const useProducts = (
  pageSize: number,
  pageIndex: number,
  productPayload?: IProductListPayload,
  staleTime = 0,
  IncludeGiftVouchers: boolean = false,
) => {
  let tempQueryKey = [QueryKeys.productList] as string[];
  if (productPayload?.categoryIds?.[0]) {
    tempQueryKey.push(productPayload.categoryIds[0]);
  }

  return useQuery<IProductList>({
    queryKey: tempQueryKey,
    queryFn: async () => {
      return await getProducts(pageSize, pageIndex, productPayload, IncludeGiftVouchers);
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: staleTime,
  });
};

export const useMemberExclusive = (pageSize: number, pageIndex: number, session?: INextAuthSessionModel | null) => {
  return useQuery<IProductList>({
    queryKey: [QueryKeys.memberExclusive],
    queryFn: async () => {
      const memberExclusiveData = await getMemberExclusive(pageSize, pageIndex, session);
      return memberExclusiveData;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: false,
  });
};

export const useCategoryProduct = (
  moduleSeName: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  module: string,
  code?: string, 
  includeOtherStores: boolean = true, 
  enabled: boolean = true,
) => {
  const setFilterLoading = useHeaderStore((state) => state.setFilterLoading);
  const openFilterPanel = useHeaderStore((state) => state.openFilterPanel);
  const setShowloaderOverlay = useHeaderStore((state) => state.setShowloaderOverlay);

  // Build query key with all relevant dependencies
  let tempQueryKey = [QueryKeys.singleModuleProductList, module, moduleSeName, sorting, includeOtherStores] as (string | boolean)[];
  
  if (module === 'category') {
    if (filters !== `{"categories":["${moduleSeName}"]}`) {
      tempQueryKey.push(filters);
    }
  } else if (module === 'brand') {
    if (filters !== `{"brandSeName":["${moduleSeName}"]}`) {
      tempQueryKey.push(filters);
    }
  }

  // Add store code to query key if provided
  if (code) {
    tempQueryKey.push(code);
  }



  const query = useQuery<ISearchProduct>({
    queryKey: tempQueryKey,
    queryFn: async () => {
      try {
        setFilterLoading(true);
        // Pass includeOtherStores to searchProduct
        return await searchProduct('', filters, pageIndex, pageSize, sorting, includeOtherStores);
      } finally {
        setFilterLoading(false);
        openFilterPanel(false);
        setShowloaderOverlay(false);
      }
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData) => previousData,
    enabled: enabled,
    // staleTime: 2 * 60 * 1000, //less than 5 minutes
  });

  return query;
};

export const useProduct = (paramSeName: string, enableCall: boolean = true, id?: string[]) => {
  let tempQueryKey = [paramSeName] as string[];
  
  if (id && id[0] !== '' && id.length > 0) {
    tempQueryKey.push(id.join(','));
  }

  return useQuery<IProductDetails>({
    queryKey: tempQueryKey,
    queryFn: async () => {
      return await getProduct(paramSeName, id);
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enableCall && !!paramSeName,
  });
};

export const useRecentlyViewedProducts = (arrayOfProductIds?: string[]) => {
  return useQuery<IProductModel[]>({
    queryKey: [QueryKeys.recentlyViewedProducts, arrayOfProductIds],
    queryFn: async () => {
      return await getRecentlyViewedProducts(arrayOfProductIds || ['']);
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!arrayOfProductIds?.length,
  });
};

export const useSearchProduct = (
  query: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  includeOtherStores: boolean = true,
) => {
  const setFilterLoading = useHeaderStore((state) => state.setFilterLoading);
  const openFilterPanel = useHeaderStore((state) => state.openFilterPanel);
  const setShowloaderOverlay = useHeaderStore((state) => state.setShowloaderOverlay);
  let queryKeys = [QueryKeys.searchProduct, query, filters, sorting, includeOtherStores];

  const searchQuery = useQuery<ISearchProduct>({
    queryKey: queryKeys,
    queryFn: async () => {
      try {
        setFilterLoading(true);
        return await searchProduct(query, filters, pageIndex, pageSize, sorting, includeOtherStores);
      } finally {
        setFilterLoading(false);
        openFilterPanel(false);
        setShowloaderOverlay(false);
      }
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData) => previousData,
  });

  return searchQuery;
};

export const useMamberExclusiveProduct = (
  query: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  session?: INextAuthSessionModel | null,
) => {
  const setFilterLoading = useHeaderStore((state) => state.setFilterLoading);
  const openFilterPanel = useHeaderStore((state) => state.openFilterPanel);
  const setShowloaderOverlay = useHeaderStore((state) => state.setShowloaderOverlay);

  const memberQuery = useQuery<ISearchProduct>({
    queryKey: [QueryKeys.memberExclusiveList, query, filters, sorting],
    queryFn: async () => {
      try {
        setFilterLoading(true);
        return await memberExclusiveList(query, filters, pageIndex, pageSize, sorting, session);
      } finally {
        setFilterLoading(false);
        openFilterPanel(false);
        setShowloaderOverlay(false);
      }
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData) => previousData,
  });

  return memberQuery;
};

export const useMemberExclusiveFilterData = (
  query: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  session?: INextAuthSessionModel | null,
) => {
  const openFilterPanel = useHeaderStore((state) => state.openFilterPanel);

  const filterQuery = useQuery<ISearchProduct>({
    queryKey: [QueryKeys.memberExclusiveList, query, filters, sorting],
    queryFn: async () => {
      try {
        return await memberExclusiveList(query, '', pageIndex, pageSize, sorting, session);
      } finally {
        openFilterPanel(false);
      }
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData) => previousData,
  });
  return filterQuery;
};

export const useFilterData = (query: string, filters: string, pageIndex: number, pageSize: number, sorting: string) => {
  const openFilterPanel = useHeaderStore((state) => state.openFilterPanel);
  let queryKeys = [QueryKeys.searchProduct, query, filters, sorting];

  const filterDataQuery = useQuery<ISearchProduct>({
    queryKey: queryKeys,
    queryFn: async () => {
      try {
        return await searchProduct(query, '', pageIndex, pageSize, sorting);
      } finally {
        openFilterPanel(false);
      }
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData) => previousData,
  });
  return filterDataQuery;
};

export const useCategoryBrandFilterData = (
  moduleSeName: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  module: string,
  code?: string, 
  includeOtherStores: boolean = true, 
  enabled: boolean = true,
) => {
  const openFilterPanel = useHeaderStore((state) => state.openFilterPanel);

    // Build query key with all relevant dependencies
  let tempQueryKey = [QueryKeys.singleModuleProductList, module, moduleSeName, sorting, includeOtherStores] as (string | boolean)[];
  
  if (module === 'category') {
    if (filters !== `{"categories":["${moduleSeName}"]}`) {
      tempQueryKey.push(filters);
    }
  } else if (module === 'brand') {
    if (filters !== `{"brandSeName":["${moduleSeName}"]}`) {
      tempQueryKey.push(filters);
    }
  }

  // Add store code to query key if provided
  if (code) {
    tempQueryKey.push(code);
  }
  const categoryBrandQuery = useQuery<ISearchProduct>({
    queryKey: tempQueryKey,
    queryFn: async () => {
      try {
        return await searchProduct('', filters, pageIndex, pageSize, sorting, includeOtherStores);
      } finally {
        openFilterPanel(false);
      }
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: false,
  });
  return categoryBrandQuery;
};

export const usePopularSearch = (searchPanel: boolean) => {
  return useQuery<any>({
    queryKey: [QueryKeys.popularSearch],
    queryFn: async () => {
      return await getPopularSearches();
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: searchPanel,
  });
};

export const useProductInfoGraphic = (paramSeName: string) => {
  return useQuery<number>({
    queryKey: [QueryKeys.getInfoGraphic + paramSeName],
    queryFn: async () => {
      return await getProductInfoGraphic(paramSeName);
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
  });
};
export const useUpdateInfoGraphic = (paramSeName: string, quickView?: boolean) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postProductInfoGraphic,
    onSuccess: () => {
      if (quickView) queryClient.invalidateQueries({ queryKey: [QueryKeys.getInfoGraphic + paramSeName] });
    },
  });
};
export const useGetBrandList = () => {
  return useQuery<IBrandList[]>({
    queryKey: [QueryKeys.brandList],
    queryFn: async () => {
      return await getBrandList();
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useSearchAutoComplete = (query: string, searchPanel: boolean) => {
  return useQuery<ISearchProduct>({
    queryKey: [QueryKeys.autoComplete, query],
    queryFn: async () => {
      let autoCompleteData = await searchAutoComplete(query);
      return autoCompleteData;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: searchPanel,
  });
};
export const useRecommendedProduct = (paramSeName: string) => {
  return useQuery<IProductModel[]>({
    queryKey: [QueryKeys.recommendedProducts + paramSeName],
    queryFn: async () => {
      return await getRecommendedProductList(paramSeName);
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!paramSeName,
  });
};
export const useCrossCategoryProduct = (paramSeName: string) => {
  return useQuery<IProductModel[]>({
    queryKey: [QueryKeys.crossCategorySuggestions + paramSeName],
    queryFn: async () => {
      return await getCrossCategorySuggestionsList(paramSeName);
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!paramSeName,
  });
};
export const useWishlistProducts = (paramSeName?: string, session?: INextAuthSessionModel | null, isAuthenticated?: boolean) => {
  return useQuery<IWishListProducts>({
    queryKey: [QueryKeys.wishlist + paramSeName],
    queryFn: async () => {
      return await getWishlist(paramSeName as string, session);
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!paramSeName && isAuthenticated,
  });
};
